import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAtomValue, useSetAtom } from "jotai";

import { errorHandler } from "#app/monitoring/errorHandler";
import { showErrorToast, showSuccessToast } from "#app/notifications";
import { quoteRequestLinesQuery } from "#modules/quoteRequestLines/quoteRequestLinesQuery";
import {
  getQuoteRequestQueryData,
  quoteRequestQuery,
  setQuoteRequestQueryData,
} from "#modules/quoteRequests/infra/quoteRequest.query";
import { updateQuoteRequestReference } from "#modules/quoteRequests/infra/updateQuoteRequestReference.api";
import { queryKeys } from "#shared/queryKeys/queryKeys";
import { quoteRequestAtom } from "#shared/view/components/AppShell/Atoms";

export const useUpdateQuoteRequestReference = () => {
  const queryClient = useQueryClient();
  const currentQuoteRequest = useAtomValue(quoteRequestAtom);
  const setQuoteRequest = useSetAtom(quoteRequestAtom);

  const { mutate, ...rest } = useMutation({
    mutationFn: updateQuoteRequestReference,
    onMutate: async ({ id, reference }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.quoteRequests.detail(id) });

      const previousQuoteRequest = getQuoteRequestQueryData(queryClient, id);

      if (previousQuoteRequest) {
        setQuoteRequestQueryData(queryClient, id, {
          ...previousQuoteRequest,
          reference,
        });
      }

      const previousAtomValue = currentQuoteRequest;
      if (currentQuoteRequest) {
        setQuoteRequest({
          ...currentQuoteRequest,
          reference,
        });
      }

      return { previousQuoteRequest, previousAtomValue, id };
    },
    onSuccess: (quoteRequest) => {
      void queryClient.invalidateQueries(quoteRequestQuery(quoteRequest?.id));
      void queryClient.invalidateQueries(quoteRequestLinesQuery());
      showSuccessToast({ title: "La référence a été mise à jour" });
    },
    onError: (err, variables, context) => {
      if (context?.previousQuoteRequest) {
        setQuoteRequestQueryData(queryClient, context.id, context.previousQuoteRequest);
      }

      if (context?.previousAtomValue !== undefined) {
        setQuoteRequest(context.previousAtomValue);
      }
      showErrorToast({
        title: "Une erreur est survenue lors de la mise à jour de la référence",
      });
      errorHandler(err);
    },
  });

  return { updateQuoteRequestReference: mutate, ...rest };
};
